// src/pages/syntramfg/SyntraMfgInnovationHub.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faRocket,
  faTools,
  faComments,
  faBoxOpen,
  faHandshake,
  faGavel,
  faUsers,
  faRobot,
  faCommentsDollar,
  faCheckCircle,
  faRupeeSign,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

/**
 * SyntraMfgInnovationHub.jsx
 * Full manufacturing refactor of your original agriculture hub.
 * - 10 portlets with dummy data & interactive placeholders
 * - responsive layout
 * - keeps UI color scheme unchanged (cyan/yellow/gray)
 */

/* ---------------------- Portlet wrapper ---------------------- */
const Portlet = ({ icon, title, desc, expanded, onToggle, children }) => {
  return (
    <div className="bg-gray-600 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition flex flex-col items-center p-4">
      <button
        onClick={onToggle}
        className="py-4 px-6 flex flex-col items-center gap-3 rounded-xl bg-cyan-700 hover:bg-cyan-800 transition w-full"
        aria-expanded={expanded}
      >
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center text-xl">
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>

        <div className="w-full">
          <div className="flex justify-between items-start gap-2">
            <div>
              <h3 className="text-lg font-semibold text-yellow-200">{title}</h3>
              <p className="text-sm text-white mt-1">{desc}</p>
            </div>
            <div className="px-4 py-2 rounded-lg text-xs bg-blue-500 text-white">
              {expanded ? "Collapse" : "Open"}
            </div>
          </div>
        </div>
      </button>

      {expanded && <div className="p-5 border-t border-gray-100 bg-cyan-50/40 w-full">{children}</div>}
    </div>
  );
};

/* -------------------- Subcomponents for portlets -------------------- */

/* 1. Knowledge Hub - search + short articles (Manufacturing) */
const KnowledgeHub = () => {
  const articles = [
    {
      id: 1,
      title: "Digital Twin 101",
      tag: "Industry 4.0",
      snippet: "Intro to digital twins for production lines — modeling, sensors, and ROI.",
    },
    {
      id: 2,
      title: "Lean Cell Design",
      tag: "Lean",
      snippet: "Practical steps to implement cell-based manufacturing for faster cycles.",
    },
    {
      id: 3,
      title: "Predictive Maintenance Basics",
      tag: "Maintenance",
      snippet: "How vibration and thermal sensors reduce downtime using simple analytics.",
    },
  ];
  const [q, setQ] = useState("");
  const results = articles.filter(
    (a) => a.title.toLowerCase().includes(q.toLowerCase()) || a.tag.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <div className="mb-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search knowledge (e.g., digital twin, OEE)"
          className="w-full p-2 border rounded"
        />
      </div>

      <div className="space-y-2">
        {results.map((a) => (
          <div key={a.id} className="bg-white p-3 rounded border">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">{a.title}</h4>
                <p className="text-sm text-gray-500">{a.tag}</p>
                <p className="text-sm text-gray-700 mt-1">{a.snippet}</p>
              </div>
              <button className="ml-3 text-sm text-white border border-cyan-200 px-3 py-1 rounded">Read</button>
            </div>
          </div>
        ))}
        {results.length === 0 && <div className="text-sm text-gray-500">No articles found.</div>}
      </div>
    </div>
  );
};

/* 2. Product Showcase - innovative manufacturing products (0-2 years) */
const ProductShowcase = () => {
  const [products] = useState([
    {
      id: 1,
      name: "Edge Controller X1",
      price: 75000,
      ageMonths: 8,
      rating: 4.6,
      desc: "Edge controller for real-time OEE telemetry and PLC bridging.",
      img: "https://via.placeholder.com/400x240.png?text=Edge+Controller+X1",
    },
    {
      id: 2,
      name: "CobotiPal - Collaborative Robot",
      price: 320000,
      ageMonths: 14,
      rating: 4.4,
      desc: "Safe cobot for assembly and pick-and-place for small batches.",
      img: "https://via.placeholder.com/400x240.png?text=CobotiPal",
    },
  ]);
  const [wish, setWish] = useState([]);
  const toggleWish = (p) => setWish((w) => (w.includes(p.id) ? w.filter((id) => id !== p.id) : [...w, p.id]));

  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        {products.map((p) => (
          <div key={p.id} className="bg-white p-3 rounded border">
            <div className="flex justify-between items-start gap-3">
              <img src={p.img} alt={p.name} className="w-36 h-24 object-cover rounded" />
              <div className="flex-1">
                <h4 className="font-semibold">{p.name}</h4>
                <p className="text-sm text-gray-500">{p.desc}</p>
                <div className="mt-2 text-sm text-gray-600">
                  Launched {p.ageMonths} months ago • <span className="text-yellow-600"><FontAwesomeIcon icon={faStar} /> {p.rating}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-cyan-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {p.price}</div>
                <button onClick={() => toggleWish(p)} className="w-32 mt-2 px-3 py-1 rounded border text-sm">
                  {wish.includes(p.id) ? "Wishlisted" : "Wishlist"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 3. Services & Solutions - consulting, digital twins, PMaaS */
const ServicesSolutions = () => {
  const services = [
    { id: 1, name: "Predictive Maintenance as a Service", pricePerMonth: 12000, desc: "Remote monitoring & anomaly alerts." },
    { id: 2, name: "Factory Automation Integration", pricePerProject: 350000, desc: "PLC + HMI + SCADA integration for brownfield plants." },
  ];
  const [requests, setRequests] = useState([]);
  const requestService = (s) => {
    const contact = prompt(`Enter contact for requesting ${s.name} (dummy):`);
    if (!contact) return;
    setRequests((r) => [...r, { id: Date.now(), service: s.name, contact }]);
    alert(`Service request sent for ${s.name} (dummy)`);
  };

  return (
    <div>
      <div className="space-y-2">
        {services.map((s) => (
          <div key={s.id} className="bg-white p-3 rounded border flex justify-between items-start">
            <div>
              <h4 className="font-semibold">{s.name}</h4>
              <p className="text-sm text-gray-500">{s.desc}</p>
            </div>
            <div className="text-right">
              <div className="text-cyan-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {s.pricePerMonth || s.pricePerProject}</div>
              <button onClick={() => requestService(s)} className="mt-2 bg-cyan-600 text-white px-3 py-1 rounded">Request</button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold">Requests (dummy)</h5>
        {requests.length === 0 ? <p className="text-sm text-gray-500">No requests yet.</p> : requests.map((r) => <div key={r.id} className="text-sm">{r.service} • {r.contact}</div>)}
      </div>
    </div>
  );
};

/* 4. Pilot Feedback & Trials (post + gallery) */
const PilotFeedback = () => {
  const [trials, setTrials] = useState([
    { id: 1, plant: "Unit A - Pune", product: "Edge Controller X1", summary: "Reduced machine idle by 12% in Week 1", pics: [] },
  ]);
  const [form, setForm] = useState({ plant: "", product: "", summary: "" });

  const submit = (e) => {
    e.preventDefault();
    setTrials((t) => [...t, { ...form, id: Date.now(), pics: [] }]);
    setForm({ plant: "", product: "", summary: "" });
    alert("Pilot feedback posted (dummy)");
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-gray-600 p-3 rounded mb-3">
        <input required value={form.plant} onChange={(e) => setForm({ ...form, plant: e.target.value })} placeholder="Plant / Unit" className="w-full p-2 border rounded mb-2" />
        <input required value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Solution / Product" className="w-full p-2 border rounded mb-2" />
        <textarea required value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Short summary" className="w-full p-2 border rounded mb-2 bg-gray-200 text-black" style={{ height: "140px", width: "100%" }} />
        <button className="w-full bg-cyan-600 text-white py-2 rounded">Post Feedback</button>
      </form>

      <div className="space-y-2">
        {trials.map((t) => (
          <div key={t.id} className="bg-white p-3 rounded border">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold">{t.product} — <span className="text-sm text-gray-500">{t.plant}</span></h4>
                <p className="text-sm text-gray-700 mt-1">{t.summary}</p>
              </div>
              <div className="text-xs text-gray-500">Photos: {t.pics.length}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 5. Sample Blueprints & Physical Sample Ask */
const SampleBlueprints = () => {
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ name: "", sample: "", address: "" });

  const submit = (e) => {
    e.preventDefault();
    const id = Date.now();
    setRequests((r) => [...r, { ...form, id, status: "Requested" }]);
    setForm({ name: "", sample: "", address: "" });
    alert("Sample / blueprint requested (dummy)");
  };

  const progress = (id) => {
    setRequests((r) => r.map((req) => (req.id === id ? { ...req, status: req.status === "Delivered" ? "Delivered" : "Dispatched" } : req)));
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-white p-3 rounded border mb-3">
        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Sample / Blueprint" value={form.sample} onChange={(e) => setForm({ ...form, sample: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Delivery address / email" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <button className="w-full bg-cyan-600 text-white py-2 rounded">Request Sample</button>
      </form>

      <div className="bg-white p-3 rounded border">
        <h5 className="font-semibold">Sample Requests</h5>
        {requests.length === 0 ? <p className="text-sm text-gray-500">No sample requests yet.</p> : requests.map((r) => (
          <div key={r.id} className="flex justify-between items-center py-2 border-b text-sm">
            <div>{r.sample} • {r.name}</div>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-600">{r.status}</div>
              <button onClick={() => progress(r.id)} className="text-sm text-blue-600">Advance</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* 6. Accelerator - pitch submission + investors list */
const Accelerator = () => {
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState({ founder: "", idea: "", contact: "" });
  const investors = [
    { id: 1, name: "IndusTech VC", focus: "Manufacturing tech & automation" },
    { id: 2, name: "MakeIndia Fund", focus: "SME digitalization" },
  ];

  const submit = (e) => {
    e.preventDefault();
    setSubmissions((s) => [...s, { ...form, id: Date.now() }]);
    setForm({ founder: "", idea: "", contact: "" });
    alert("Pitch submitted (dummy)");
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-gray-600 p-3 rounded mb-3">
        <input required placeholder="Founder / Team" value={form.founder} onChange={(e) => setForm({ ...form, founder: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Idea / Product name" value={form.idea} onChange={(e) => setForm({ ...form, idea: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <input required placeholder="Contact email/phone" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="w-full p-2 border rounded mb-2" />
        <button className="w-full bg-cyan-600 text-white py-2 rounded">Submit Pitch</button>
      </form>

      <div className="bg-white p-3 rounded border mb-3">
        <h5 className="font-semibold">Investor Partners</h5>
        {investors.map((inv) => (
          <div key={inv.id} className="flex justify-between text-sm py-1 border-b">
            <div>{inv.name}</div>
            <div className="text-gray-500">{inv.focus}</div>
          </div>
        ))}
      </div>

      <div className="bg-white p-3 rounded border">
        <h5 className="font-semibold">Submissions (dummy)</h5>
        {submissions.length === 0 ? <p className="text-sm text-gray-500">No submissions yet.</p> : submissions.map((s) => <div key={s.id} className="text-sm border-b py-1">{s.founder} • {s.idea}</div>)}
      </div>
    </div>
  );
};

/* 7. Policy & Compliance corner (list) */
const PolicyCorner = () => {
  const policies = [
    { id: 1, title: "PLI for Advanced Mfg", snippet: "Mock summary: incentives for high-tech assembly lines." },
    { id: 2, title: "Energy Efficiency Norms", snippet: "Guidelines for energy audits and savings for SME plants." },
  ];
  return (
    <div>
      {policies.map((p) => (
        <div key={p.id} className="bg-white p-3 rounded border mb-2">
          <h4 className="font-semibold">{p.title}</h4>
          <p className="text-sm text-gray-500">{p.snippet}</p>
          <div className="mt-2 text-right">
            <button className="text-sm text-white">Read</button>
          </div>
        </div>
      ))}
    </div>
  );
};

/* 8. Community & Collaboration (challenges, hackathons) */
const CommunityCollab = () => {
  const [challenges] = useState([
    { id: 1, title: "Low-cost IoT for Machines", prize: 100000, deadline: "2025-11-30" },
    { id: 2, title: "Modular Fixturing Design", prize: 50000, deadline: "2025-10-10" },
  ]);
  return (
    <div>
      {challenges.map((c) => (
        <div key={c.id} className="bg-white p-3 rounded border mb-2 flex justify-between items-start">
          <div>
            <h4 className="font-semibold">{c.title}</h4>
            <p className="text-sm text-gray-500">Deadline: {c.deadline}</p>
          </div>
          <div className="text-right">
            <div className="text-cyan-700 font-semibold"><FontAwesomeIcon icon={faRupeeSign} /> {c.prize}</div>
            <button className="w-40 mt-2 bg-cyan-600 text-white px-3 py-1 rounded">Participate</button>
          </div>
        </div>
      ))}
    </div>
  );
};

/* 9. Smart Tools (simple AI Advisor simulation) */
const SmartTools = () => {
  const [q, setQ] = useState("");
  const [resp, setResp] = useState("");
  const ask = (e) => {
    e.preventDefault();
    const t = q.toLowerCase();
    let r = "Try our Knowledge Hub or request a sample blueprint.";
    if (t.includes("oee")) r = "Target OEE improvement: Focus on availability losses — schedule maintenance and quick changeovers.";
    if (t.includes("downtime")) r = "Check spindle bearings and tool wear; add vibration monitoring for early alerts.";
    if (t.includes("energy")) r = "Consider VFDs for large motors and schedule idle-time power down.";
    setResp(r);
  };

  return (
    <div>
      <form onSubmit={ask} className="flex gap-2 mb-3">
        <div className="flex flex-col w-full">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask (e.g., how to reduce downtime?)" className="flex-1 w-full p-2 border rounded" />
          <button type="submit" className="bg-cyan-600 text-white px-3 py-2 rounded mt-2 w-28">Ask</button>
        </div>
      </form>
      {resp && <div className="bg-white p-3 rounded border text-sm">{resp}</div>}
    </div>
  );
};

/* 10. Discussion Circles (simple threads) */
const DiscussionCircles = () => {
  const [threads, setThreads] = useState([
    { id: 1, title: "Best low-cost vibration sensors", posts: 4 },
    { id: 2, title: "Lean vs. Automation: When to choose?", posts: 9 },
  ]);
  const [title, setTitle] = useState("");
  const create = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setThreads((t) => [{ id: Date.now(), title, posts: 0 }, ...t]);
    setTitle("");
  };

  return (
    <div>
      <form onSubmit={create} className="mb-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Start a discussion (thread)" className="w-full p-2 border rounded" />
      </form>
      <div className="space-y-2">
        {threads.map((th) => (
          <div key={th.id} className="bg-white p-3 rounded border flex justify-between">
            <div>
              <h4 className="font-semibold">{th.title}</h4>
              <p className="text-sm text-gray-500">{th.posts} posts</p>
            </div>
            <div className="flex items-center gap-2">
              <button className="w-24 text-sm bg-cyan-500 text-white">Open</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------- Innovation Hub Container ------------------- */

export default function SyntraMfgInnovationHub() {
  const [active, setActive] = useState(null);

  const useCases = [
    { id: "knowledge", title: "Knowledge Hub", desc: "Wiki, explainers & curated research", icon: faBook, component: <KnowledgeHub /> },
    { id: "products", title: "Product Showcase", desc: "Launchpad (0–2 years) & reviews", icon: faRocket, component: <ProductShowcase /> },
    { id: "services", title: "Services & Solutions", desc: "Digital twin, PMaaS & system integration", icon: faTools, component: <ServicesSolutions /> },
    { id: "feedback", title: "Pilot Feedback & Trials", desc: "Plant trial reports & ratings", icon: faComments, component: <PilotFeedback /> },
    { id: "samples", title: "Sample Blueprints", desc: "Request blueprints & physical samples", icon: faBoxOpen, component: <SampleBlueprints /> },
    { id: "accelerator", title: "Accelerator", desc: "Pitch, investors & funding", icon: faHandshake, component: <Accelerator /> },
    { id: "policy", title: "Policy & Compliance", desc: "Safety, ESG & certification", icon: faGavel, component: <PolicyCorner /> },
    { id: "community", title: "Community & Challenges", desc: "Hackathons & collaboration", icon: faUsers, component: <CommunityCollab /> },
    { id: "smart", title: "Smart Tools (AI Advisor)", desc: "Ask an AI advisor for quick suggestions", icon: faRobot, component: <SmartTools /> },
    { id: "circles", title: "Discussion Circles", desc: "Plant/tech-specific discussion threads", icon: faCommentsDollar, component: <DiscussionCircles /> },
  ];

  return (
    <div>
      <section className="min-h-screen bg-gradient-to-r from-cyan-700 to-cyan-800 py-12 px-4 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-yellow-200 flex items-center justify-center gap-3">
              <span className="text-3xl bg-cyan-100 text-yellow-200 rounded-full w-12 h-12 flex items-center justify-center">💡</span>
              SyntraMFG Innovation Hub
            </h1>
            <p className="mt-3 text-white max-w-3xl mx-auto">
              Discover and pilot breakthrough manufacturing innovations, connect with startups, request samples, and collaborate with engineers and plant managers.
            </p>
          </header>

          {/* Innovative Product Launched Section */}
          <div className="bg-gray-600 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition p-6 mb-8">
            <h2 className="text-2xl font-bold text-yellow-200 mb-6 text-center">
              🚀 Innovative Product Launched
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Product Card 1 */}
              <div className="bg-white rounded-xl shadow p-5 flex flex-col justify-between">
                <div>
                  <img
                    src="https://via.placeholder.com/300x180.png?text=Smart+Conveyor+System"
                    alt="Smart Conveyor System"
                    className="rounded-lg mb-4"
                  />
                  <h3 className="text-lg font-semibold text-cyan-700">Smart Conveyor System</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Adaptive speed conveyor with inline sensors to detect jams and optimize throughput.
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded-lg">Enquiry</button>
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg">Order</button>
                  <button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-lg">Contact</button>
                </div>
              </div>

              {/* Product Card 2 */}
              <div className="bg-white rounded-xl shadow p-5 flex flex-col justify-between">
                <div>
                  <img
                    src="https://via.placeholder.com/300x180.png?text=CNC+Starter+Kit"
                    alt="CNC Starter Kit"
                    className="rounded-lg mb-4"
                  />
                  <h3 className="text-lg font-semibold text-cyan-700">CNC Starter Kit</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Entry-level CNC package with training and modular add-ons for SMEs.
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded-lg">Enquiry</button>
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg">Order</button>
                  <button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-lg">Contact</button>
                </div>
              </div>

              {/* Product Card 3 */}
              <div className="bg-white rounded-xl shadow p-5 flex flex-col justify-between">
                <div>
                  <img
                    src="https://via.placeholder.com/300x180.png?text=Predictive+Sensor+Kit"
                    alt="Predictive Sensor Kit"
                    className="rounded-lg mb-4"
                  />
                  <h3 className="text-lg font-semibold text-cyan-700">Predictive Sensor Kit</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Vibration & temp sensor set for predictive maintenance pilots.
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded-lg">Enquiry</button>
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg">Order</button>
                  <button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-lg">Contact</button>
                </div>
              </div>
            </div>
          </div>

          {/* Portlets Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((u) => (
              <Portlet
                key={u.id}
                icon={u.icon}
                title={u.title}
                desc={u.desc}
                expanded={active === u.id}
                onToggle={() => setActive((s) => (s === u.id ? null : u.id))}
              >
                {u.component}
                <div className="mt-4 text-xs text-yellow-200">Tip: Click to expand — all content is dummy.</div>
              </Portlet>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-cyan-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link to="/about-us" className="text-white hover:text-cyan-400 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-cyan-400 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-cyan-400 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-cyan-400 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
